/**
 * Helpers de formatage — Comparison foundation (Chunk 9.Q.1 mini-polish UX).
 *
 * Presentation-only : humanise les `field_key` techniques + formate les valeurs
 * jsonb pour un affichage broker lisible (pas de raw JSON / debug). Aucune
 * logique métier, aucune normalisation (ADR 0026 = chunks ultérieurs).
 */

import { resolveCellKind, toFiniteNumber, type FieldKind } from "@kollectivia/shared";

/**
 * Humanise un field_key canonique pour affichage.
 *
 *   "date_document"                  → "Date document"
 *   "prime_totale_mensuelle_base"    → "Prime totale mensuelle base"
 *   "vie_base.salary_multiple"       → "Vie base › Salary multiple"
 *
 * Segments séparés par `.` → joints par « › ». Mots séparés par `_` → espace +
 * première lettre capitalisée (le reste préservé pour ne pas casser les
 * acronymes éventuels). Le field_key brut reste affiché en sous-titre côté UI
 * (vérité préservée).
 */
// 9.AU.1 — l'implémentation vit désormais dans `@kollectivia/shared` : le worker
// doit nommer un champ dans un courriel, et il ne peut pas importer une page web.
// Réexporté ici pour que les importateurs existants restent inchangés.
export { humanizeFieldKey } from "@kollectivia/shared";

/**
 * Classe une valeur jsonb pour le rendu cellule.
 *   - "empty"   : null / undefined
 *   - "scalar"  : string / number / boolean
 *   - "array"   : tableau (rendu en chips)
 *   - "object"  : objet (rendu résumé clé:valeur borné)
 */
export type CellValueKind = "empty" | "scalar" | "array" | "object";

export function classifyValue(value: unknown): CellValueKind {
  if (value === null || value === undefined) return "empty";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") return "object";
  return "scalar";
}

/** Rend un scalaire (string/number/boolean) en texte propre. */
export function formatScalar(value: unknown): string {
  if (typeof value === "boolean") return value ? "✓" : "✗";
  return String(value);
}

/**
 * Aplati un item d'array pour affichage en chip. Les objets imbriqués sont
 * résumés (`{n} champs`) plutôt que dumpés en JSON brut.
 */
export function formatArrayItem(item: unknown): string {
  if (item === null || item === undefined) return "—";
  if (Array.isArray(item)) return `[${item.length}]`;
  if (typeof item === "object") {
    const n = Object.keys(item as Record<string, unknown>).length;
    return `{${n}}`;
  }
  return formatScalar(item);
}

/** Entrées top-level d'un objet pour résumé `clé: valeur` borné. */
export function objectEntries(value: unknown): Array<{ key: string; display: string }> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value as Record<string, unknown>).map(([key, v]) => ({
    key,
    display: formatArrayItem(v),
  }));
}

/* ============================================================================
 * 9.S.1 — Normalisation Phase 1 (format-only, zéro recalcul, zéro conversion).
 *
 * Doctrine PO :
 *   - Format-only Phase 1. Aucune mutation de la valeur source.
 *   - Allowlist EXPLICITE par `field_key` + dispatch primaire sur `unit` API.
 *   - Locale stricte fr-CA / en-CA pour le format monétaire/percent/date.
 *   - Fallback `text` strict = anti-régression garantie (zéro touch valeur).
 *   - ADR 0026 axe B Phase 1 : pas de scoring, pas de projection, pas de
 *     normalisation analytique. Cosmétique display uniquement.
 * ============================================================================
 */

/**
 * Locale strictement bornée pour Intl.* dans la page Comparison. Mapping
 * fr → fr-CA / en → en-CA (tenants Kollectivia = brokers Québec).
 */
export type ComparisonLocale = "fr-CA" | "en-CA";

export function toComparisonLocale(lang: string | undefined | null): ComparisonLocale {
  if (lang && lang.toLowerCase().startsWith("en")) return "en-CA";
  return "fr-CA";
}

/**
 * Résultat de `formatComparisonValue` — `display` = chaîne prête à afficher,
 * `suppressUnit` = `true` quand le format incorpore déjà la marque (currency
 * `$`, percent `%`) pour éviter le doublon avec le suffix `unit` historique.
 * `raw` = chaîne sérialisée de la valeur source pour tooltip natif `title`.
 */
export interface FormattedCellValue {
  display: string;
  raw: string;
  kind: FieldKind;
  /** True si le kind incorpore la marque unit (currency_cad / percent). */
  suppressUnit: boolean;
}

/**
 * Format-only, frontend pur. JAMAIS de mutation `value` source. En cas de
 * mismatch type ou kind=text → fallback strict `formatScalar(value)`.
 */
export function formatComparisonValue(
  value: unknown,
  fieldKey: string,
  unit: string | null | undefined,
  locale: ComparisonLocale,
): FormattedCellValue {
  const raw = value === null || value === undefined ? "" : String(value);
  const kind = resolveCellKind(fieldKey, unit ?? null);

  // Fallback : kind text OU value non-numérique pour kinds numériques.
  if (kind === "text") {
    return { display: formatScalar(value), raw, kind, suppressUnit: false };
  }

  if (
    kind === "currency_cad" ||
    kind === "percent" ||
    kind === "number_multiple" ||
    kind === "number"
  ) {
    const num = toFiniteNumber(value);
    if (num === null) {
      return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
    }

    if (kind === "percent") {
      // Anti-régression : si la valeur dépasse 1, le contrat percent est
      // ambigu (95 vs 0.95). Bypass → rendu historique unit suffix.
      if (num > 1) {
        return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
      }
      try {
        const fmt = new Intl.NumberFormat(locale, {
          style: "percent",
          maximumFractionDigits: 2,
        });
        return { display: fmt.format(num), raw, kind, suppressUnit: true };
      } catch {
        return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
      }
    }

    if (kind === "currency_cad") {
      try {
        const fmt = new Intl.NumberFormat(locale, {
          style: "currency",
          currency: "CAD",
          currencyDisplay: "symbol",
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        });
        return { display: fmt.format(num), raw, kind, suppressUnit: true };
      } catch {
        return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
      }
    }

    if (kind === "number_multiple") {
      try {
        const fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
        return { display: `${fmt.format(num)}×`, raw, kind, suppressUnit: true };
      } catch {
        return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
      }
    }

    // number
    try {
      const fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
      return { display: fmt.format(num), raw, kind, suppressUnit: false };
    } catch {
      return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
    }
  }

  if (kind === "date_iso") {
    if (typeof value !== "string") {
      return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
    }
    const parsed = parseIsoDate(value);
    if (!parsed) {
      return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
    }
    try {
      // Force UTC pour les dates pures YYYY-MM-DD : new Date("2026-06-12")
      // est interprété UTC midnight, ce qui dérive d'1 jour en arrière dans
      // les fuseaux horaires Québec (UTC-4/-5). Format UTC = vérité ISO.
      const fmt = new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeZone: "UTC",
      });
      return { display: fmt.format(parsed), raw, kind, suppressUnit: false };
    } catch {
      return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
    }
  }

  return { display: formatScalar(value), raw, kind: "text", suppressUnit: false };
}

function parseIsoDate(value: string): Date | null {
  // Accepte YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss(.sss)?(Z|±HH:mm)?. Strict.
  if (!/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/.test(value)) {
    return null;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// ---------------------------------------------------------------------------
// Typage d'un critère — DÉPLACÉ vers `@kollectivia/shared` (chunk 9.AJ.1)
// ---------------------------------------------------------------------------
//
// Le scoring normalise selon le TYPE du critère et doit le lire au même endroit que
// l'affichage. Un second typage côté serveur aurait fini par ne plus parler du même
// champ que l'écran. Réexporté ici pour que les appelants existants — et les tests
// 9.S.1 — ne bougent pas d'une ligne.

export {
  fieldKind,
  resolveCellKind,
  parseLocalizedNumericString,
  toFiniteNumber,
} from "@kollectivia/shared";
export type { FieldKind } from "@kollectivia/shared";
